/**
 * Validation utilities for form inputs
 */

/**
 * Validates Iranian mobile number
 * Must start with 09 or 9 and be 10 or 11 digits total
 */
export const validateMobileNumber = (mobile: string): { isValid: boolean; message: string } => {
  if (!mobile) {
    return { isValid: false, message: 'شماره موبایل الزامی است' };
  }

  // Remove any non-digit characters
  const cleanMobile = mobile.replace(/\D/g, '');

  // Check if it starts with 09 and is 11 digits
  if (/^09\d{9}$/.test(cleanMobile)) {
    return { isValid: true, message: '' };
  }

  // Check if it starts with 9 and is 10 digits
  if (/^9\d{9}$/.test(cleanMobile)) {
    return { isValid: true, message: '' };
  }

  return { 
    isValid: false, 
    message: 'شماره موبایل معتبر نیست' 
  };
};

/**
 * Validates Iranian National ID (کد ملی)
 * Must be exactly 10 digits and pass the check digit algorithm
 */
export const validateIranianNationalCode = (code: string): { isValid: boolean; message: string } => {
  if (!code) {
    return { isValid: false, message: 'کد ملی الزامی است' };
  }

  // Remove any non-digit characters
  const cleanCode = code.replace(/\D/g, '');

  // Check if it's exactly 10 digits
  if (!/^\d{10}$/.test(cleanCode)) {
    return { isValid: false, message: 'کد ملی باید دقیقاً 10 رقم باشد' };
  }

  // Check for codes with all same digits (invalid)
  if (/^(\d)\1{9}$/.test(cleanCode)) {
    return { isValid: false, message: 'کد ملی نامعتبر است' };
  }

  const digits = cleanCode.split('').map(Number);
  const control = digits[9];
  
  // Calculate sum of products
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += digits[i] * (10 - i);
  }

  const remainder = sum % 11;

  // Apply the validation rule:
  // If remainder < 2, control digit should equal remainder
  // Otherwise, control digit should equal (11 - remainder)
  let isValid = false;
  if (remainder < 2) {
    isValid = control === remainder;
  } else {
    isValid = control === (11 - remainder);
  }

  return {
    isValid,
    message: isValid ? '' : 'کد ملی نامعتبر است'
  };
};

/**
 * Formats mobile number for display (adds spaces for readability)
 */
export const formatMobileNumber = (mobile: string): string => {
  const cleanMobile = mobile.replace(/\D/g, '');
  
  if (cleanMobile.length === 11 && cleanMobile.startsWith('09')) {
    return `${cleanMobile.slice(0, 4)} ${cleanMobile.slice(4, 7)} ${cleanMobile.slice(7)}`;
  }
  
  if (cleanMobile.length === 10 && cleanMobile.startsWith('9')) {
    return `0${cleanMobile.slice(0, 3)} ${cleanMobile.slice(3, 6)} ${cleanMobile.slice(6)}`;
  }
  
  return mobile;
};

/**
 * Formats national ID for display (adds dashes for readability)
 */
export const formatNationalId = (nationalId: string): string => {
  const cleanId = nationalId.replace(/\D/g, '');
  
  if (cleanId.length === 10) {
    return `${cleanId.slice(0, 3)}-${cleanId.slice(3, 9)}-${cleanId.slice(9)}`;
  }
  
  return nationalId;
};

/**
 * Cleans input by removing non-digit characters
 */
export const cleanDigitsOnly = (input: string): string => {
  return input.replace(/\D/g, '');
};

/**
 * Validates email format
 */
export const validateEmail = (email: string): { isValid: boolean; message: string } => {
  if (!email) {
    return { isValid: true, message: '' }; // Email is optional in most cases
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  return {
    isValid: emailRegex.test(email),
    message: emailRegex.test(email) ? '' : 'فرمت ایمیل صحیح نیست'
  };
};

/**
 * Validates Persian/English name (no numbers or special characters)
 */
export const validateName = (name: string): { isValid: boolean; message: string } => {
  if (!name || !name.trim()) {
    return { isValid: false, message: 'نام الزامی است' };
  }

  // Allow Persian, English letters and spaces
  const nameRegex = /^[a-zA-Zآ-ی\s]+$/;
  
  return {
    isValid: nameRegex.test(name.trim()),
    message: nameRegex.test(name.trim()) ? '' : 'نام فقط می‌تواند شامل حروف فارسی یا انگلیسی باشد'
  };
}; 